import { Select, Stack, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateDynamicDropdown,
  useUpdateDynamicDropdown,
} from "../hooks/useDynamicDropdowns"
import type {
  DynamicDropdown,
  DynamicDropdownFormValues,
} from "../models/dynamicDropdown"
import {
  DROPDOWN_TYPES,
  DynamicDropdownFormSchema,
} from "../models/dynamicDropdown"

interface Props {
  item?: DynamicDropdown
  onSuccess: () => void
}

const DynamicDropdownFormContent = forwardRef<{ submit: () => void }, Props>(
  ({ item, onSuccess }, ref) => {
    const { t } = useTranslation()
    const createMutation = useCreateDynamicDropdown()
    const updateMutation = useUpdateDynamicDropdown()
    const isEdit = !!item

    const form = useForm<DynamicDropdownFormValues>({
      initialValues: { type: 5, option: "" },
      validate: (values) => {
        const result = DynamicDropdownFormSchema.safeParse(values)
        if (!result.success) {
          return Object.fromEntries(
            result.error.issues.map((e) => [e.path[0], e.message]),
          )
        }
        return {}
      },
    })

    useEffect(() => {
      if (item) {
        form.setValues({ type: item.type, option: item.option })
      }
    }, [item])

    const handleSubmit = (values: DynamicDropdownFormValues) => {
      if (isEdit && item) {
        updateMutation.mutate(
          { id: item.id, values },
          {
            onSuccess: () => {
              notifications.show({
                color: "green",
                title: t("common.success"),
                message: t("dynamicDropdowns.updateSuccess"),
              })
              onSuccess()
            },
            onError: () => {
              notifications.show({
                color: "red",
                title: t("common.error"),
                message: t("common.error"),
              })
            },
          },
        )
      } else {
        createMutation.mutate(values, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("dynamicDropdowns.createSuccess"),
            })
            onSuccess()
          },
          onError: () => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: t("common.error"),
            })
          },
        })
      }
    }

    const handleFormSubmit = form.onSubmit(handleSubmit)
    useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }), [
      handleFormSubmit,
    ])

    return (
      <form onSubmit={handleFormSubmit}>
        <Stack p="md">
          <Select
            label={t("dynamicDropdowns.type")}
            placeholder={t("dynamicDropdowns.type")}
            data={DROPDOWN_TYPES.map((t) => ({
              value: String(t.value),
              label: t.label,
            }))}
            withAsterisk
            value={String(form.values.type)}
            onChange={(val) =>
              form.setFieldValue("type", val ? Number(val) : 5)
            }
            error={form.errors.type as string}
          />
          <TextInput
            label={t("dynamicDropdowns.option")}
            placeholder={t("dynamicDropdowns.option")}
            withAsterisk
            {...form.getInputProps("option")}
          />
        </Stack>
      </form>
    )
  },
)

export default DynamicDropdownFormContent
